<!-- filepath: d:\programming\vuejs-components\Vc Website Components\src\components\queryapp\FeedbackDialog.vue -->
<template>
  
    <div v-if="show" class="feedback-dialog-wrapper">
      <!-- Backdrop -->
      <div class="modal-backdrop fade show" @click="close"></div>
      
      <!-- Modal -->
      <div class="modal fade show" style="display: block;" tabindex="-1" 
           aria-labelledby="feedbackModalLabel" aria-modal="true" 
           @keydown.esc="close">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="feedbackModalLabel">
                <i class="bi bi-chat-dots me-2"></i>{{ title }}
              </h5>
              <button type="button" class="btn-close" @click="close" aria-label="Close"></button>
            </div>
            
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-bold">{{ question }}</label>
                <div class="btn-group w-100" role="group" aria-label="Feedback rating">
                  <!-- Yes button -->
                  <input type="radio" class="btn-check" name="feedbackRating" id="feedback-yes" 
                         v-model="localFeedback.rating" :value="5" autocomplete="off">
                  <label class="btn btn-outline-success" for="feedback-yes">
                    <i class="bi bi-hand-thumbs-up me-1"></i> Yes
                  </label>
                  
                  <!-- No button -->
                  <input type="radio" class="btn-check" name="feedbackRating" id="feedback-no" 
                         v-model="localFeedback.rating" :value="1" autocomplete="off">
                  <label class="btn btn-outline-danger" for="feedback-no">
                    <i class="bi bi-hand-thumbs-down me-1"></i> No
                  </label>
                  
                  <!-- Partially button -->
                  <input type="radio" class="btn-check" name="feedbackRating" id="feedback-partial" 
                         v-model="localFeedback.rating" :value="3" autocomplete="off">
                  <label class="btn btn-outline-warning" for="feedback-partial">
                    <i class="bi bi-exclamation-triangle me-1"></i> Partially
                  </label>
                </div>
              </div>
              
              <div class="mb-3">
                <label class="form-label">Additional comments (optional):</label>
                <textarea v-model="localFeedback.comments" 
                          class="form-control" 
                          rows="3"
                          maxlength="500"
                          :placeholder="placeholder"></textarea>
                <small class="text-muted">
                  {{ localFeedback.comments.length }}/500 characters
                </small>
              </div>
              
              <div v-if="localFeedback.rating !== 5 && localFeedback.rating !== null" 
                   class="alert alert-info py-2 px-3 mb-0">
                <small>
                  <i class="bi bi-info-circle me-1"></i>
                  {{ helpText }}
                </small>
              </div>
            </div>
            
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="close">
                Cancel
              </button>
              <button type="button" 
                      class="btn btn-primary" 
                      @click="submit"
                      :disabled="!localFeedback.rating">
                <i class="bi bi-send me-1"></i> Submit Feedback
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

</template>

<script>
import { sanitizeText } from '@/utils/sanitizeInput';

export default {
  name: 'FeedbackDialog',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Feedback'
    },
    question: {
      type: String,
      default: 'Did this feature work for you?'
    },
    placeholder: {
      type: String,
      default: 'What worked well? What could be improved?'
    },
    helpText: {
      type: String,
      default: 'Your feedback helps us improve.'
    },
    featureName: {
      type: String,
      default: 'Feature'
    }
  },
  
  emits: ['close', 'submit'],
  
  data() {
    return {
      localFeedback: {
        rating: null,
        comments: ''
      }
    };
  },
  
  computed: {

  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        // Reset form when opening
        this.localFeedback = {
          rating: null,
          comments: ''
        };
        
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      } else {
        // Re-enable body scroll when modal closes
        document.body.style.overflow = '';
      }
    }
  },
  
  methods: {
    close() {
      this.$emit('close');
    },
    
    async submit() {
      if (!this.localFeedback.rating) return;
      
      // Sanitize comments
      const sanitizedComments = sanitizeText(this.localFeedback.comments, 500);
      
      const feedbackData = {
        rating: this.localFeedback.rating,
        comments: sanitizedComments,
        featureName: this.featureName,
        timestamp: new Date().toISOString()
      };
      
      // Emit the feedback data
      this.$emit('submit', feedbackData);
      
      // Log for debugging
      console.log('Feedback submitted:', feedbackData);
      
      // Submit to server if communicator is available
      if (window.vcCommunicator?.submitfeedback) {
        try {
          await window.vcCommunicator.submitfeedback(
            this.featureName, 
            feedbackData.rating, 
            feedbackData.comments
          );
        } catch (error) {
          console.error('Error submitting feedback:', error);
        }
      }
      
      // Close the dialog
      this.close();
    }
  },
  
  beforeUnmount() {
    // Ensure body scroll is restored
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.feedback-dialog-wrapper {
  position: fixed;
  z-index: 1050;
}

.modal {
  z-index: 1055;
}

.btn-check {
  position: absolute;
  clip: rect(0,0,0,0);
  pointer-events: none;
}

.btn-check:checked + .btn {
  color: var(--bs-btn-active-color);
  background-color: var(--bs-btn-active-bg);
  border-color: var(--bs-btn-active-border-color);
}

.btn-check:checked + .btn-outline-success {
  color: #fff;
  background-color: #198754;
  border-color: #198754;
}

.btn-check:checked + .btn-outline-danger {
  color: #fff;
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-check:checked + .btn-outline-warning {
  color: #000;
  background-color: #ffc107;
  border-color: #ffc107;
}

/* Character counter styling */
.text-muted {
  font-size: 0.875rem;
}

/* Ensure modal appears above other content */
.modal-backdrop {
  z-index: 1050;
}

.modal {
  z-index: 1055;
}
</style>